import { getMcActiveOrgId, loadMorpheusImportSnapshot, loadMorpheusImportSnapshotsForScopes, saveMorpheusImportSnapshot, saveMorpheusImportSnapshotAlias, subscribeMorpheusImportSnapshot } from "./mcCloudSync";
import { idbGetJson, idbSetJson, idbRemoveItem } from "./mcStorageEngine";
import { withInventoryDeadline } from "./mcInventoryOperation";
import { buildPreparedData, parseCsv } from "./mcEngineData";
import {
  loadActiveImportSnapshotCache,
  saveActiveImportSnapshotCache,
  clearActiveImportSnapshotCache,
} from "./mcImportSnapshotCache";

export const MC_ACTIVE_INVENTORY_EVENT = "mc-active-inventory-changed";

// Sprint I1 / Storage Engine Phase 2:
// Active inventory now uses the unified matrixos-morpheus IndexedDB database.
// The legacy dedicated database is read once for a safe in-place migration and
// then removed. Firestore remains the shared source of truth.
const STORAGE_PREFIX = "activeInventory.v2";
const MEMORY_KEY = "__MC_ACTIVE_INVENTORY__";
const LEGACY_DB_NAME = "matrixos-morpheus-active-inventory";
const LEGACY_DB_VERSION = 1;
const LEGACY_STORE_NAME = "snapshots";

function normalizeScope(scope = "ALL") {
  const raw = String(scope || "ALL").trim();
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  const cleaned = raw
    .replace(/^inventory_/i, "")
    .replace(/_Store$/i, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return cleaned || "ALL";
}

function orgKey() {
  return String(getMcActiveOrgId?.() || "local").trim() || "local";
}

function recordKey(scope = "ALL") {
  return `${orgKey()}::${normalizeScope(scope)}`;
}

function storageKey(scope = "ALL") {
  return `${STORAGE_PREFIX}:${recordKey(scope)}`;
}

function memory() {
  if (typeof window === "undefined") return null;
  if (!window[MEMORY_KEY]) window[MEMORY_KEY] = Object.create(null);
  return window[MEMORY_KEY];
}

function snapshotTime(snapshot = null) {
  if (!snapshot) return 0;
  return Date.parse(snapshot.uploadedAt || snapshot.updatedAt || snapshot.syncedAt || "") || 0;
}

function validSnapshot(snapshot = null) {
  if (!(snapshot && Array.isArray(snapshot.rawRows) && snapshot.rawRows.length > 0)) return false;
  const activeOrg = orgKey();
  const snapshotOrg = String(snapshot.__mcOrgId || snapshot.orgId || snapshot.organisationId || "").trim();
  // Legacy snapshots without an org marker are unsafe once a real org has
  // resolved. They are ignored rather than risking cross-organisation data.
  if (activeOrg !== "local" && snapshotOrg !== activeOrg) return false;
  return true;
}

// Cached source rows are immutable by manifest ID. Shared global aliases should
// parse the 3,300-row CSV only once, not ten times for ten site selectors.
// Keep this bounded and tenant-qualified; pointers remain the cloud authority.
const parsedCsvManifestCache = new Map();
function parseManifestCsv(snapshot, activeOrg) {
  const manifestId = String(snapshot?.snapshotManifest?.manifestId || "").trim();
  const cacheKey = manifestId ? `${activeOrg}::${manifestId}` : "";
  if (cacheKey && parsedCsvManifestCache.has(cacheKey)) return parsedCsvManifestCache.get(cacheKey);
  const parsed = parseCsv(snapshot.csvText || "");
  if (cacheKey && parsed.rows?.length) {
    parsedCsvManifestCache.set(cacheKey, parsed);
    while (parsedCsvManifestCache.size > 4) parsedCsvManifestCache.delete(parsedCsvManifestCache.keys().next().value);
  }
  return parsed;
}

function normalizeTrustedCloudSnapshot(snapshot = null) {
  // Firestore reads are already isolated by orgs/{activeOrgId}/morpheus. Older
  // snapshots created before tenant markers were introduced are therefore safe
  // to adopt from this trusted path, but must be stamped before they enter any
  // browser cache. Local/browser snapshots without a matching marker remain
  // rejected by validSnapshot().
  if (!snapshot || typeof snapshot !== "object") return null;
  const activeOrg = orgKey();
  const storedOrg = String(snapshot.__mcOrgId || snapshot.orgId || snapshot.organisationId || "").trim();
  if (storedOrg && storedOrg !== activeOrg) return null;

  // MC498 — CSV source is stored once in Firestore. Rehydrate both derived
  // row projections on demand, using the existing RFC4180-compatible parser
  // and the exact saved mapping/config/rules. Older full-row manifests and
  // non-CSV/POS imports continue to work without modification.
  let hydrated = snapshot;
  if (snapshot.snapshotEncoding === "csv-source-v1") {
    try {
      const parsed = parseManifestCsv(snapshot, activeOrg);
      if (!parsed.rows?.length || (snapshot.rowCount && parsed.rows.length !== Number(snapshot.rowCount))) {
        console.warn("MC import snapshot CSV hydration rejected: row count mismatch", {
          importId: snapshot.importId, expected: snapshot.rowCount, actual: parsed.rows?.length || 0,
        });
        return null;
      }
      hydrated = {
        ...snapshot,
        parsedHeaders: parsed.headers,
        rawRows: parsed.rows,
        preparedRows: snapshot.activeImportPointer?.alias ? []
          : buildPreparedData(parsed.rows, snapshot.mapping, snapshot.config, snapshot.rules),
      };
    } catch (error) {
      console.warn("MC import snapshot CSV hydration failed", { importId: snapshot.importId, error });
      return null;
    }
  }
  if (!(Array.isArray(hydrated.rawRows) && hydrated.rawRows.length > 0)) return null;
  return { ...hydrated, __mcOrgId: activeOrg, orgId: activeOrg };
}


function hasScopedInventoryValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function normalizeSiteProjectionValue(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^inventory[_\s-]*/i, "")
    .replace(/[_\s-]*(store|site|outlet)$/i, "")
    .replace(/[^a-z0-9]/g, "");
}

function inferRowSiteProjection(snapshot = null, scope = "ALL") {
  const rows = Array.isArray(snapshot?.rawRows) ? snapshot.rawRows : [];
  if (!rows.length) return { header: "", value: "" };
  const wanted = normalizeSiteProjectionValue(scope);
  if (!wanted || wanted === "all") return { header: "", value: "" };

  const headers = Array.isArray(snapshot?.parsedHeaders) && snapshot.parsedHeaders.length
    ? snapshot.parsedHeaders
    : Object.keys(rows[0] || {});
  const scored = headers.map((header) => {
    const label = String(header || "");
    const headerToken = label.toLowerCase();
    const matches = rows.reduce((count, row) => (
      normalizeSiteProjectionValue(row?.[header]) === wanted ? count + 1 : count
    ), 0);
    const semantic = /(^|[^a-z])(site|store|branch|outlet|location)([^a-z]|$)/i.test(headerToken) ? 100000 : 0;
    return { header: label, matches, score: semantic + matches };
  }).filter((item) => item.matches > 0);

  scored.sort((a, b) => b.score - a.score);
  return scored.length ? { header: scored[0].header, value: scope } : { header: "", value: "" };
}

function projectAliasSnapshotForScope(snapshot = null, scope = "ALL") {
  if (!snapshot || !Array.isArray(snapshot.rawRows)) return snapshot;
  const pointer = snapshot.activeImportPointer || {};
  if (!pointer.alias) return snapshot;

  const inventoryHeader = String(pointer.inventoryHeader || snapshot?.mapping?.systemSoh || "").trim();
  const inferredRowSite = inferRowSiteProjection(snapshot, scope);
  const rowSiteHeader = String(pointer.rowSiteHeader || snapshot?.distributionContext?.rowSiteHeader || inferredRowSite.header || "").trim();
  const sourceSiteValue = String(pointer.sourceSiteValue || snapshot?.distributionContext?.sourceSiteValue || inferredRowSite.value || "").trim();

  let projectedRows = snapshot.rawRows;
  if (rowSiteHeader && sourceSiteValue) {
    const wanted = normalizeSiteProjectionValue(sourceSiteValue);
    projectedRows = snapshot.rawRows.filter((row) =>
      row && typeof row === "object" && normalizeSiteProjectionValue(row?.[rowSiteHeader]) === wanted
    );
  } else if (inventoryHeader) {
    projectedRows = snapshot.rawRows.filter((row) =>
      row && typeof row === "object" && hasScopedInventoryValue(row?.[inventoryHeader])
    );
  }

  // Alias snapshots always describe a site-scoped view of one immutable global
  // manifest. Row-based exports must be sliced by the source Site field; wide
  // exports are sliced by the site's inventory_<site> column. This distinction
  // is what prevents a 3,300-row All Sites import from leaking into a 330-row
  // site's Intake/Review workspace.
  if (!projectedRows.length) return snapshot;

  return {
    ...snapshot,
    scope: normalizeScope(scope),
    rawRows: projectedRows,
    preparedRows: [],
    rowCount: projectedRows.length,
    mapping: { ...(snapshot.mapping || {}), ...(inventoryHeader ? { systemSoh: inventoryHeader } : {}) },
    activeImportAliasProjection: true,
  };
}

function chooseNewest(...items) {
  return items
    .filter(validSnapshot)
    .sort((a, b) => {
      const timeDelta = snapshotTime(b) - snapshotTime(a);
      if (timeDelta) return timeDelta;
      const aId = String(a?.activeImportId || a?.importId || "");
      const bId = String(b?.activeImportId || b?.importId || "");
      if (aId !== bId && b?.activeImportPointer) return 1;
      return Number(b?.rowCount || b?.rawRows?.length || 0) - Number(a?.rowCount || a?.rawRows?.length || 0);
    })[0] || null;
}

function openLegacyDb() {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) return resolve(null);
    const request = window.indexedDB.open(LEGACY_DB_NAME, LEGACY_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LEGACY_STORE_NAME)) db.createObjectStore(LEGACY_STORE_NAME, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

async function readLegacyLocal(scope = "ALL") {
  const db = await openLegacyDb();
  if (!db) return null;
  const key = recordKey(scope);
  const result = await new Promise((resolve) => {
    try {
      const tx = db.transaction(LEGACY_STORE_NAME, "readonly");
      const req = tx.objectStore(LEGACY_STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result?.snapshot || null);
      req.onerror = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  });
  try { db.close(); } catch (_) {}
  return validSnapshot(result) ? result : null;
}

async function removeLegacyLocal(scope = "ALL") {
  const db = await openLegacyDb();
  if (!db) return false;
  const key = recordKey(scope);
  const ok = await new Promise((resolve) => {
    try {
      const tx = db.transaction(LEGACY_STORE_NAME, "readwrite");
      tx.objectStore(LEGACY_STORE_NAME).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (_) {
      resolve(false);
    }
  });
  try { db.close(); } catch (_) {}
  return ok;
}

async function readLocal(scope = "ALL") {
  const key = recordKey(scope);
  const mem = memory();
  if (mem?.[key] && validSnapshot(mem[key])) return mem[key];

  // Read both durable browser stores. The route/import cache is intentionally
  // scope-based and therefore also protects startup when the organisation id
  // is resolved slightly later than the active site.
  const [unified, routeCache] = await Promise.all([
    idbGetJson(storageKey(scope), null).catch(() => null),
    loadActiveImportSnapshotCache(scope).catch(() => null),
  ]);
  const durable = chooseNewest(unified, routeCache);
  if (validSnapshot(durable)) {
    if (mem) mem[key] = durable;
    // Self-heal either durable store when only one side survived.
    if (durable !== unified) {
      idbSetJson(storageKey(scope), durable, {
        type: "active-inventory-snapshot",
        scope: normalizeScope(scope),
        orgId: orgKey(),
        recoveredFrom: "route-import-cache",
        storagePhase: "I1.2.1",
      }).catch(() => {});
    }
    if (durable !== routeCache) saveActiveImportSnapshotCache(scope, durable).catch(() => {});
    return durable;
  }

  // One-time, feature-safe migration from the Phase 1 dedicated database.
  const legacy = await readLegacyLocal(scope);
  if (!validSnapshot(legacy)) return null;
  await Promise.allSettled([
    idbSetJson(storageKey(scope), legacy, {
      type: "active-inventory-snapshot",
      scope: normalizeScope(scope),
      orgId: orgKey(),
      migratedFrom: LEGACY_DB_NAME,
      storagePhase: "I1.2.1",
    }),
    saveActiveImportSnapshotCache(scope, legacy),
  ]);
  if (mem) mem[key] = legacy;
  removeLegacyLocal(scope).catch(() => {});
  return legacy;
}

async function writeLocal(scope = "ALL", snapshot = {}) {
  const key = recordKey(scope);
  const normalized = {
    ...snapshot,
    __mcOrgId: orgKey(),
    orgId: orgKey(),
    scope: normalizeScope(scope),
    rowCount: Number(snapshot.rowCount ?? snapshot.rawRows?.length ?? 0),
    columnCount: Number(snapshot.columnCount ?? snapshot.parsedHeaders?.length ?? 0),
  };
  const mem = memory();
  if (mem) mem[key] = normalized;

  // Persist to two independent IndexedDB paths before reporting success. The
  // unified store is the Storage Engine authority; the established route cache
  // is a recovery copy and protects refresh/startup identity timing.
  const [unifiedResult, routeResult] = await Promise.allSettled([
    idbSetJson(storageKey(scope), normalized, {
      type: "active-inventory-snapshot",
      scope: normalizeScope(scope),
      orgId: orgKey(),
      storagePhase: "I1.2.1",
    }),
    saveActiveImportSnapshotCache(scope, normalized),
  ]);
  return unifiedResult.status === "fulfilled" ||
    (routeResult.status === "fulfilled" && routeResult.value !== false);
}

function broadcast(scope, snapshot) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MC_ACTIVE_INVENTORY_EVENT, {
    detail: { scope: normalizeScope(scope), importId: snapshot?.activeImportId || snapshot?.importId || "", rowCount: snapshot?.rawRows?.length || 0 },
  }));
}

export function peekActiveInventorySnapshot(scope = "ALL") {
  const value = memory()?.[recordKey(scope)] || null;
  return validSnapshot(value) ? value : null;
}

export async function loadActiveInventorySnapshot(scope = "ALL", { refreshCloud = true } = {}) {
  const local = await withInventoryDeadline(() => readLocal(scope), "Reading the inventory device cache", 5000)
    .catch(() => peekActiveInventorySnapshot(scope));
  if (!refreshCloud) return local;
  const cloudRaw = normalizeTrustedCloudSnapshot(
    await loadMorpheusImportSnapshot(scope).catch(() => null),
  );
  const cloud = projectAliasSnapshotForScope(cloudRaw, scope);
  const localProjected = projectAliasSnapshotForScope(local, scope);
  const chosen = chooseNewest(localProjected, cloud);
  if (cloud && chosen === cloud) await writeLocal(scope, cloud);
  return chosen;
}


export async function loadActiveInventorySnapshotsForScopes(scopes = []) {
  const requested = Array.from(new Set(
    (Array.isArray(scopes) ? scopes : [])
      .map((scope) => String(scope || "").trim())
      .filter(Boolean)
  ));
  if (!requested.length) return {};

  const localPairs = await Promise.all(requested.map(async (scope) => [
    scope,
    await readLocal(scope).catch(() => null),
  ]));
  const cloud = await loadMorpheusImportSnapshotsForScopes(requested).catch(() => ({}));
  const result = {};

  for (const [scope, local] of localPairs) {
    const trustedCloudRaw = normalizeTrustedCloudSnapshot(cloud?.[scope] || null);
    const trustedCloud = projectAliasSnapshotForScope(trustedCloudRaw, scope);
    const localProjected = projectAliasSnapshotForScope(local, scope);
    const chosen = chooseNewest(localProjected, trustedCloud);
    if (!chosen) continue;
    result[scope] = chosen;
    if (trustedCloud && chosen === trustedCloud) await writeLocal(scope, trustedCloud);
  }
  return result;
}

export async function commitActiveInventorySnapshot(scope = "ALL", snapshot = {}, options = {}) {
  const assertActive = options.assertActive || (() => {});
  assertActive();
  // New intake payloads are not tenant-stamped until this function normalizes
  // them. Validate only their inventory shape here; using validSnapshot() at
  // this point rejects every legitimate upload once a real org is active.
  if (!(snapshot && Array.isArray(snapshot.rawRows) && snapshot.rawRows.length > 0)) {
    return { ok: false, reason: "empty-snapshot" };
  }
  const uploadedAt = snapshot.uploadedAt || snapshot.updatedAt || new Date().toISOString();
  const importId = String(snapshot.importId || snapshot.activeImportId || `import-${Date.now()}`);
  const normalized = {
    ...snapshot,
    __mcOrgId: orgKey(),
    orgId: orgKey(),
    importId,
    activeImportId: importId,
    uploadedAt,
    updatedAt: snapshot.updatedAt || uploadedAt,
    rowCount: Number(snapshot.rowCount ?? snapshot.rawRows.length),
    columnCount: Number(snapshot.columnCount ?? snapshot.parsedHeaders?.length ?? Object.keys(snapshot.rawRows[0] || {}).length),
  };

  // Secure the shared authority before replacing any browser's active cache.
  // A blocked/private-mode device cache must not prevent the shared write.
  // CSV text already contains the complete source inventory. The previous
  // cloud payload stored the same 3,300 rows as CSV + rawRows + preparedRows,
  // tripling transfer/work and saturating the Firestore client. Keep the full
  // in-memory/IndexedDB snapshot, but write only one reversible CSV source.
  // For POS/non-CSV imports retain the established full-row storage format.
  const isCsvSource = String(normalized?.config?.dataSourceMode || "").toLowerCase() === "csv"
    && typeof normalized.csvText === "string" && normalized.csvText.trim().length > 0;
  const cloudPayload = isCsvSource ? {
    ...normalized,
    snapshotEncoding: "csv-source-v1",
    rawRows: [],
    preparedRows: [],
  } : normalized;
  const cloudResult = await saveMorpheusImportSnapshot(scope, cloudPayload, { assertActive }).catch((err) => ({ ok: false, reason: "cloud-write-failed", err }));
  assertActive();
  const cloudOk = Boolean(cloudResult?.ok);
  let localOk = false;
  if (cloudOk) {
    // writeLocal installs the same-tab memory handoff before its first await.
    localOk = await withInventoryDeadline(() => writeLocal(scope, normalized), "Saving the inventory device cache", 5000).catch(() => false);
    assertActive();
    broadcast(scope, normalized);
  }
  return { ok: cloudOk, localOk: Boolean(localOk), cloudOk, sharedOk: cloudOk, cloudResult, snapshot: normalized };
}


export async function commitActiveInventoryAlias(scope = "ALL", alias = {}, sourceSnapshot = null, options = {}) {
  const assertActive = options.assertActive || (() => {});
  assertActive();
  const result = await saveMorpheusImportSnapshotAlias(scope, alias, { assertActive }).catch((err) => ({ ok: false, reason: "alias-write-failed", err }));
  assertActive();
  if (result?.ok && validSnapshot(sourceSnapshot)) {
    const inventoryHeader = String(alias?.inventoryHeader || "");
    const baseNormalized = {
      ...sourceSnapshot,
      scope: normalizeScope(scope),
      importId: String(alias?.aliasImportId || `${sourceSnapshot.importId || sourceSnapshot.activeImportId || "import"}__${normalizeScope(scope)}`),
      activeImportId: String(alias?.aliasImportId || `${sourceSnapshot.activeImportId || sourceSnapshot.importId || "import"}__${normalizeScope(scope)}`),
      mapping: { ...(sourceSnapshot.mapping || {}), ...(inventoryHeader ? { systemSoh: inventoryHeader } : {}) },
      activeImportAlias: true,
      activeImportPointer: {
        alias: true,
        inventoryHeader,
        rowSiteHeader: String(alias?.rowSiteHeader || ""),
        sourceSiteValue: String(alias?.sourceSiteValue || ""),
        activeImportId: String(alias?.aliasImportId || ""),
        sourceScope: String(alias?.sourceScope || "ALL"),
        distributionBatchId: String(alias?.distributionBatchId || ""),
      },
      distributionContext: {
        ...(sourceSnapshot.distributionContext || {}),
        mode: "SITE",
        scope: normalizeScope(scope),
        inheritedFrom: String(alias?.sourceScope || "ALL"),
        rowSiteHeader: String(alias?.rowSiteHeader || ""),
        sourceSiteValue: String(alias?.sourceSiteValue || ""),
        distributionBatchId: String(alias?.distributionBatchId || ""),
      },
    };
    const normalized = projectAliasSnapshotForScope(baseNormalized, scope);
    // The shared pointer is confirmed and writeLocal installs the same-tab
    // snapshot immediately. Optional durable device copies must not hold up
    // the site revision or make a confirmed distribution time out.
    writeLocal(scope, normalized).catch(() => false);
    broadcast(scope, normalized);
  }
  return result;
}

export async function clearActiveInventorySnapshot(scope = "ALL") {
  const key = recordKey(scope);
  const mem = memory();
  if (mem) delete mem[key];
  const results = await Promise.allSettled([
    idbRemoveItem(storageKey(scope)),
    clearActiveImportSnapshotCache(scope),
  ]);
  removeLegacyLocal(scope).catch(() => {});
  return results.some((result) => result.status === "fulfilled");
}

export function subscribeActiveInventorySnapshot(scope = "ALL", onChange = () => {}, onError = () => {}) {
  return subscribeMorpheusImportSnapshot(
    scope,
    async (incomingCloudSnapshot) => {
      const cloudRaw = normalizeTrustedCloudSnapshot(incomingCloudSnapshot);
      if (!cloudRaw) return;
      const cloudSnapshot = projectAliasSnapshotForScope(cloudRaw, scope);
      const local = projectAliasSnapshotForScope(await readLocal(scope), scope);
      const chosen = chooseNewest(local, cloudSnapshot);
      if (chosen === cloudSnapshot) {
        await writeLocal(scope, cloudSnapshot);
        broadcast(scope, cloudSnapshot);
      }
      onChange(chosen || cloudSnapshot);
    },
    onError,
  );
}
